import React, { useState } from "react";
import {
  Box,
  Tooltip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Backdrop,
} from "@mui/material";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import InputIcon from "@mui/icons-material/Input";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { addUser } from "../api"; // Importação para chamada de API
import ImportUsersModal from "./ImportUsersModal"; // Importação do modal de importação de usuários
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import ManagerUserModal from "./ManagerUserModal";
import { getAllUsersAndFoldersPermissions } from "../api"; // Adicione essa linha

interface AdminToolbarProps {
  onRefreshUsers: () => void;
  setStatusMessage: React.Dispatch<React.SetStateAction<string>>;
}

const AdminToolbar: React.FC<AdminToolbarProps> = ({
  onRefreshUsers,
  setStatusMessage,
}) => {
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [importUserModalOpen, setImportUserModalOpen] = useState(false); // Estado para controlar o modal de importação
  const [userToAdd, setUserToAdd] = useState<string>("");
  const [isAddingUser, setIsAddingUser] = useState(false); // Controle do estado de adição de usuário
  const [isSyncing, setIsSyncing] = useState(false);
  const [isCooldown, setIsCooldown] = useState(false);
  const [managerUserModalOpen, setManagerUserModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Controle de loading
  const [userFoldersData, setUserFoldersData] = useState<any[]>([]); // Dados dos usuários e pastas

  // Abre o modal para adicionar usuário
  const handleOpenAddUserModal = () => {
    setUserToAdd("");
    setAddUserModalOpen(true);
  };

  // Fecha o modal de adicionar usuário
  const handleCloseAddUserModal = () => {
    setAddUserModalOpen(false);
  };

  // Abre o modal para importar usuários
  const handleOpenImportUserModal = () => {
    setImportUserModalOpen(true);
  };

  // Fecha o modal de importar usuários
  const handleCloseImportUserModal = () => {
    setImportUserModalOpen(false);
  };

  const handleOpenManagerUserModal = async () => {
    setIsLoading(true); // Exibe o loading enquanto a API está sendo chamada
    try {
      const username = sessionStorage.getItem("username") || ""; // Obtém o username do administrador
      const data = await getAllUsersAndFoldersPermissions(username); // Chama a API
      setUserFoldersData(data); // Define os dados retornados no estado
      setManagerUserModalOpen(true); // Abre o modal
    } catch (error: any) {
      toast.error(error.message || "Erro ao carregar dados de usuários.");
    } finally {
      setIsLoading(false); // Oculta o loading após a chamada
    }
  };

  const handleCloseManagerUserModal = () => {
    setManagerUserModalOpen(false);
  };

  // Adiciona usuário via API
  const handleAddUser = async () => {
    if (!userToAdd) {
      toast.error("Por favor, informe o nome ou CPF do usuário.");
      return;
    }

    setIsAddingUser(true);

    try {
      const storedUser = sessionStorage.getItem("user");
      const adminUser = storedUser ? JSON.parse(storedUser).login : "";

      if (!adminUser) throw new Error("Administrador não autenticado.");

      await addUser(adminUser, userToAdd);
      toast.success(`Usuário '${userToAdd}' adicionado com sucesso!`);
    } catch (error: any) {
      toast.error(
        `${error.message} Lista atualizada.` || "Erro ao adicionar o usuário."
      );
    } finally {
      setIsAddingUser(false);
      setAddUserModalOpen(false);
      onRefreshUsers();
    }
  };

  // Função para sincronizar/atualizar a lista de usuários
  const handleSyncUsers = async () => {
    setIsSyncing(true);
    setIsCooldown(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 1500)); // Simula o delay do processamento
      onRefreshUsers();
      toast.success("Lista de usuários atualizada com sucesso!");
    } catch (error) {
      toast.error("Erro ao atualizar a lista de usuários.");
    } finally {
      setIsSyncing(false);
    }

    // Define o cooldown de 1 minuto
    setTimeout(() => {
      setIsCooldown(false);
      setStatusMessage(""); // Limpa a mensagem após o cooldown
    }, 30000);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        backgroundColor: "transparent",
      }}
    >
      {/* Adicionar Usuário */}
      <Tooltip title="Adiciona um novo usuário ao GDU." arrow placement="top">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            color: "inherit",
            "&:hover": { color: "#1976d2" },
          }}
          onClick={handleOpenAddUserModal}
        >
          <PersonAddAltOutlinedIcon />
          <Typography variant="body2">Novo usuário</Typography>
        </Box>
      </Tooltip>

      <Box
        sx={{
          borderLeft: "1px solid gray",
          height: "18px",
        }}
      />

      {/* Importar Usuários */}
      <Tooltip title="Importa usuários de uma lista" arrow placement="top">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            color: "inherit",
            "&:hover": { color: "#1976d2" },
          }}
          onClick={handleOpenImportUserModal} // Abre o modal de importação
        >
          <InputIcon />
          <Typography variant="body2">Importar usuários</Typography>
        </Box>
      </Tooltip>

      {/* Separador */}
      <Box sx={{ borderLeft: "1px solid gray", height: "18px" }} />

      <Tooltip title="Atualizar a lista de Usuários." arrow placement="top">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: isCooldown ? "not-allowed" : "pointer",
            color: (theme) =>
              isCooldown
                ? theme.palette.mode === "light"
                  ? "#c0c0c0"
                  : "#8c8c8c"
                : "inherit",
            "&:hover": {
              color: (theme) =>
                isCooldown
                  ? theme.palette.mode === "light"
                    ? "#c0c0c0"
                    : "#8c8c8c"
                  : "#1976d2",
            },
          }}
          onClick={!isSyncing && !isCooldown ? handleSyncUsers : undefined}
          onMouseEnter={() => {
            if (isCooldown) {
              setStatusMessage(
                "Aguarde 30 segundos antes de atualizar novamente."
              );
            }
          }}
          onMouseLeave={() => {
            if (isCooldown) {
              setStatusMessage("");
            }
          }}
        >
          <CloudSyncIcon />
          <Typography variant="body2">
            {isSyncing ? "Atualizando..." : "Atualizar"}
          </Typography>
        </Box>
      </Tooltip>

      {/* Modal para Adicionar Usuário */}
      <Dialog open={addUserModalOpen} onClose={handleCloseAddUserModal}>
        <Backdrop
          open={isLoading} // Exibe o loading enquanto a API está sendo chamada
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <CircularProgress />
        </Backdrop>

        <DialogTitle>
          Adicionar Usuário
          <IconButton
            aria-label="close"
            onClick={handleCloseAddUserModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Digite o Login do usuário a ser cadastrado:
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Login"
            type="text"
            fullWidth
            variant="outlined"
            value={userToAdd}
            onChange={(e) => setUserToAdd(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end", gap: 2 }}>
  {/* Botão Cancelar */}
  <Button
    onClick={handleCloseAddUserModal}
    disabled={isAddingUser}
    sx={{
      backgroundColor: "transparent",
      color: (theme) =>
        theme.palette.mode === "dark" ? "#1976d2" : "#1976d2", // Azul para ambos os temas
      "&:hover": {
        backgroundColor: "rgba(25, 118, 210, 0.1)", // Fundo azul suave no hover
      },
      "&:disabled": {
        color: "#cccccc", // Cinza claro quando desativado
        backgroundColor: "transparent", // Sem fundo
      },
    }}
  >
    Cancelar
  </Button>

  {/* Botão Confirmar */}
  <Button
    onClick={handleAddUser}
    disabled={!userToAdd || isAddingUser}
    sx={{
      backgroundColor: "transparent",
      color: (theme) =>
        theme.palette.mode === "dark" ? "#1976d2" : "#1976d2", // Azul para ambos os temas
      "&:hover": {
        backgroundColor: "rgba(25, 118, 210, 0.1)", // Fundo azul suave no hover
      },
      "&:disabled": {
        color: "#cccccc", // Cinza claro quando desativado
        backgroundColor: "transparent", // Sem fundo
      },
    }}
  >
    Confirmar
  </Button>
</DialogActions>

      </Dialog>

      {/* Modal para Importar Usuários */}
      <ImportUsersModal
        open={importUserModalOpen}
        onClose={handleCloseImportUserModal}
        onRefreshUsers={onRefreshUsers} // Passa a função de atualização
      />
<ManagerUserModal
  open={managerUserModalOpen}
  onClose={handleCloseManagerUserModal}
  userFoldersData={userFoldersData}
  setUserFoldersData={setUserFoldersData}
  refreshAdminUsers={onRefreshUsers} // Passa a função correta
/>
    </Box>
  );
};

export default AdminToolbar;
